/* eslint-disable react-refresh/only-export-components */
import { ChatReaction } from '@src/api/shared/community/chatReactionApiClient';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ReactionContextType = {
  reactions: Record<number, ChatReaction[]>;
  updateReactionUsers: (chatId: number, emoji: string, users: ChatReaction['users']) => void;
  forceUpdateReactions: (chatId: number, reactions: ChatReaction[]) => void;
  updateReactionsOptimistically: (
    chatId: number,
    emoji: string,
    currentReactions?: ChatReaction[],
  ) => void;
  clearReactions: () => void;
};

const ReactionContext = createContext<ReactionContextType | undefined>(undefined);

export const useReactionContext = () => {
  const context = useContext(ReactionContext);
  if (!context) {
    throw new Error('useReactionContext must be used within ReactionProvider');
  }
  return context;
};

type ReactionProviderProps = {
  children: React.ReactNode;
};

// 楽観更新の保護時間（ミリ秒）
const OPTIMISTIC_UPDATE_PROTECTION_TIME = 1500;

// リアクション状態を一元管理するProvider
export const ReactionProvider = ({ children }: ReactionProviderProps) => {
  const [reactions, setReactions] = useState<Record<number, ChatReaction[]>>({});
  const [optimisticTimestamps, setOptimisticTimestamps] = useState<Record<string, number>>({});

  // 特定の絵文字のユーザー情報のみを更新
  const updateReactionUsers = useCallback(
    (chatId: number, emoji: string, users: ChatReaction['users']) => {
      setReactions((prev) => {
        const currentReactions = prev[chatId] || [];
        const updatedReactions = currentReactions.map((reaction) => {
          if (reaction.emoji === emoji) {
            return {
              ...reaction,
              users,
            };
          }
          return reaction;
        });
        return {
          ...prev,
          [chatId]: updatedReactions,
        };
      });
    },
    [],
  );

  // ポーリング用：楽観更新を上書きして強制更新
  const forceUpdateReactions = useCallback(
    (chatId: number, newReactions: ChatReaction[]) => {
      const optimisticTime = optimisticTimestamps[chatId];
      const now = Date.now();

      // 楽観更新の保護時間内はポーリング更新をスキップ
      if (optimisticTime && now - optimisticTime <= OPTIMISTIC_UPDATE_PROTECTION_TIME) {
        return;
      }

      setReactions((prev) => ({
        ...prev,
        [chatId]: newReactions,
      }));

      // タイムスタンプをクリア
      setOptimisticTimestamps((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [chatId]: _, ...rest } = prev;
        return rest;
      });
    },
    [optimisticTimestamps],
  );

  // 楽観更新：ユーザーのリアクション操作を即座にUIに反映
  const updateReactionsOptimistically = useCallback(
    (chatId: number, emoji: string, currentReactions?: ChatReaction[]) => {
      const normalizedEmoji = emoji.toLowerCase();

      setOptimisticTimestamps((prev) => ({
        ...prev,
        [chatId]: Date.now(),
      }));

      setReactions((prev) => {
        const reactionsToUse = prev[chatId] || currentReactions || [];
        const existingReaction = reactionsToUse.find((r) => r.emoji === normalizedEmoji);

        // そのemojiのリアクションが存在しない場合：新規作成
        if (!existingReaction) {
          return {
            ...prev,
            [chatId]: [
              ...reactionsToUse,
              {
                emoji: normalizedEmoji,
                count: 1,
                is_reacted: true,
                users: [],
              },
            ],
          };
        }

        // そのemojiに自分が既にリアクション済みの場合：自分のリアクションを削除
        if (existingReaction.is_reacted) {
          const updatedReactions = reactionsToUse
            .map((r) =>
              r === existingReaction ? { ...r, is_reacted: false, count: r.count - 1 } : r,
            )
            .filter((r) => r.count > 0 || r.is_reacted);

          return {
            ...prev,
            [chatId]: updatedReactions,
          };
        }

        // そのemojiに他人がリアクション済みだが自分は未リアクションの場合：自分のリアクションを追加
        const updatedReactions = reactionsToUse.map((r) =>
          r === existingReaction ? { ...r, is_reacted: true, count: r.count + 1 } : r,
        );

        return {
          ...prev,
          [chatId]: updatedReactions,
        };
      });
    },
    [],
  );

  const clearReactions = useCallback(() => {
    setReactions({});
  }, []);

  const value = useMemo(
    () => ({
      reactions,
      updateReactionUsers,
      forceUpdateReactions,
      updateReactionsOptimistically,
      clearReactions,
    }),
    [
      reactions,
      updateReactionUsers,
      forceUpdateReactions,
      updateReactionsOptimistically,
      clearReactions,
    ],
  );

  return <ReactionContext.Provider value={value}>{children}</ReactionContext.Provider>;
};
